import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { toast } from "react-toastify";
import {
  useDeleteHistoryControl,
  useUpdateHistoryControl,
} from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { HistoryControlType } from "../types/historyControl";
import { showErrorToast } from "../utils/errorToast";
import { transformDate2Front } from "../utils/transformValue";

type HistoryItemProps = {
  history: HistoryControlType;
  canEditRisk: boolean;
  refetchHistoryItems: () => void;
  refetchRiskTheme: () => void;
};

export const HistoryItem: React.FC<HistoryItemProps> = ({
  history,
  canEditRisk,
  refetchHistoryItems,
  refetchRiskTheme,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm<HistoryControlType>({
    defaultValues: { ...history },
  });

  const { designSystem } = useDesignSystem();

  // Dentro do componente HistoryItem
  useEffect(() => {
    reset({
      id: history.id,
      description: history.description,
      date: history.date,
    });
  }, [history]);

  const { mutate: updateHistoryControl } = useUpdateHistoryControl();

  const onSubmit = (data: HistoryControlType) => {
    // metodo update
    const request = {
      id: data.id,
      date: data.date,
      description: data.description,
    };

    updateHistoryControl(request, {
      onSuccess: () => {
        refetchRiskTheme();
        refetchHistoryItems();
        setIsOpen(false);
        reset(request);
        toast.success("Historico salvo com sucesso");
      },
      onError: (error) => {
        showErrorToast(error, "Erro ao salvar histórico");
        console.log("Erro ao salvar histórico"); //criar modal erro(mensagem)
      },
    });
  };

  const { mutate: deleteHistoryControl } = useDeleteHistoryControl();

  const deleteButton = () => {
    if (
      window.confirm(
        `${i18next.t("delete_control_confirmation")}\n${i18next.t(
          "history_date"
        )}: ${transformDate2Front(history.date)}`
      )
    ) {
      // metodo
      deleteHistoryControl(history.id, {
        onSuccess: () => {
          refetchHistoryItems();
          setIsOpen(false);
          toast.success("Histórico deletado com sucesso");
        },
        onError: (error) => {
          showErrorToast(error, "Erro: Histórico não deletado");
          console.log("erro ao deletar historico"); // tsk criar modal erro(mensagem)
        },
      });
    }
  };

  const labelNamesCss =
    "font-openSans font-bold text-[13px] leading-[16px] tracking-[0.18px] min-w-[7rem]";
  return (
    <div className='mb-2'>
      <div
        className='flex items-center px-2 py-[3px] cursor-pointer'
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className='min-w-[7rem] font-OpenSans font-bold text-[13px] leading-[16px]'>
          {transformDate2Front(history.date)}
        </span>
        <span className='line-clamp-1 font-OpenSans font-bold text-[13px] leading-[16px]'>
          {" "}
          {history.description}
        </span>
        <span className='ml-auto'>
          {isOpen ? (
            <MdExpandLess className='h-6 w-6' />
          ) : (
            <MdExpandMore className='h-6 w-6' />
          )}
        </span>
      </div>

      <hr />
      {isOpen && (
        <form onSubmit={handleSubmit(onSubmit)} className='p-2'>
          <div className='mb-2'>
            <label className={labelNamesCss}>
              {i18next.t("history_date")}:
            </label>
            <Controller
              name='date'
              control={control}
              render={({ field }) => (
                <input
                  type='date'
                  {...field}
                  disabled={!canEditRisk}
                  className='border rounded px-2 py-1 w-full'
                />
              )}
            />
          </div>

          <div className='mb-2'>
            <label className={labelNamesCss}>
              {i18next.t("history_progress")}:
            </label>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={2}
                  disabled={!canEditRisk}
                  className='border rounded px-2 py-1 w-full'
                />
              )}
            />
          </div>

          {canEditRisk && (
            <div className='flex justify-end gap-2 mb-2'>
              <button
                type='button'
                className='h-[32px] w-[81px] border rounded-md bg-red-600 text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                onClick={deleteButton}
              >
                {i18next.t("button_delete")}
              </button>

              <button
                type='submit'
                className='h-[32px] w-[81px] border rounded-md text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                style={{
                  backgroundColor: designSystem?.cssBasics.primaryColor,
                }}
              >
                {i18next.t("button_save")}
              </button>
            </div>
          )}

          <hr />
        </form>
      )}
    </div>
  );
};
